import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutorenewIcon from "@mui/icons-material/Autorenew";

interface UpdatePopupProps {
  open: boolean;
  onClose: () => void;
  onProceed: (comment: string) => void; // pass comment upwards
  placeName: string;
}

export default function UpdatePopup({
  open,
  onClose,
  onProceed,
  placeName,
}: UpdatePopupProps) {
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const textFieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleViewportChange = () => {
      if (window.visualViewport) {
        // If the viewport height returns to 90%+ of window height, keyboard is closed
        if (window.visualViewport.height >= window.innerHeight * 0.9) {
          setIsFocused(false);
        }
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleViewportChange);
      return () => {
        window.visualViewport?.removeEventListener(
          "resize",
          handleViewportChange,
        );
      };
    }
  }, []);

  const handleSubmit = () => {
    if (!comment.trim()) {
      setError("Comment is required to proceed");
      return;
    }
    onProceed(comment.trim());

    handleClose();
  };
  const handleClose = () => {
    setComment("");
    setError("");
    setIsFocused(false);
    onClose();
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setComment(e.target.value);
    if (error && e.target.value.trim()) {
      setError("");
    }
  };

  const handleTextFieldFocus = () => {
    setIsFocused(true);
    // Scroll the text field into view after a brief delay to ensure keyboard is visible
    setTimeout(() => {
      if (textFieldRef.current) {
        textFieldRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 300);
  };

  const handleTextFieldBlur = () => {
    setIsFocused(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: { xs: "4vw", sm: 4 },
          px: { xs: "4vw", sm: 3 },
          py: { xs: "3vw", sm: 2 },
          maxWidth: 420,
          width: "100%",
          zIndex: 9999,
          // Move dialog up when focused on mobile
          position: "relative",
          top: isFocused ? { xs: "-15vh", sm: "0" } : "0",
          transition: "top 0.3s ease",
        },
      }}
      sx={{
        zIndex: 9998,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          zIndex: 9997,
        },
      }}
      scroll="paper" // Enables scrolling within the dialog content
    >
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        {/* Close button */}
        <Box display="flex" justifyContent="flex-end">
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              bgcolor: "grey.100",
              "&:hover": { bgcolor: "grey.200" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Circular Icon */}
        <Box display="flex" justifyContent="center" mb={3} mt={1}>
          <Box
            sx={{
              width: { xs: "18vw", sm: 60 },
              height: { xs: "18vw", sm: 60 },
              borderRadius: "50%",
              background: "linear-gradient(135deg,#7b5fff,#8a2be2)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              boxShadow: "0 6px 16px rgba(138,43,226,0.35)",
            }}
          >
            <AutorenewIcon sx={{ fontSize: { xs: "10vw", sm: 45 }, color: "#fff" }} />
          </Box>
        </Box>

        {/* Title / Message */}
        <Typography
          textAlign="center"
          fontSize={{ xs: "4.5vw", sm: 20 }}
          fontWeight={600}
          color="text.primary"
          mb={1}
        >
          Update Contribution
        </Typography>

        <Typography
          textAlign="center"
          fontSize={{ xs: "3.5vw", sm: 16 }}
          color="text.primary"
          mb={3}
        >
          <strong>{placeName}</strong> already has contributions. Please
          describe what updates or changes you've noticed.
        </Typography>

        {/* Comment Input */}
        <TextField
          ref={textFieldRef}
          fullWidth
          multiline
          placeholder="Describe the updates or changes you've noticed *"
          value={comment}
          onChange={handleCommentChange}
          onFocus={handleTextFieldFocus}
          onBlur={handleTextFieldBlur}
          rows={3}
          error={!!error}
          helperText={error}
          required
          InputProps={{
            sx: {
              borderRadius: 3,
              fontSize: "max(16px, 3.76vw)", // Prevent iOS zoom (min 16px) while keeping responsive size
            },
          }}
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              "&:hover fieldset": { borderColor: "grey.500" },
              "&.Mui-focused fieldset": { borderColor: "#8a2be2" },
            },
            "& .MuiOutlinedInput-input": {
              WebkitOverflowScrolling: "touch",
              overflowY: "auto",
              overscrollBehavior: "contain",
            },
          }}
        />

        {/* Proceed Button */}
        <Button
          variant="contained"
          onClick={handleSubmit}
          fullWidth
          disabled={!comment.trim()}
          sx={{
            borderRadius: { xs: "6vw", sm: "25px" },
            py: { xs: "2vw", sm: 1.2 },
            mb: { xs: "2vw", sm: 2 },
            textTransform: "none",
            fontWeight: 600,
            fontSize: { xs: "3.8vw", sm: 16 },
            width: { xs: "50%", sm: "40%" },
            mx: "auto",
            display: "block",
            background: comment.trim()
              ? "linear-gradient(135deg,#8a2be2,#6a5acd)"
              : "linear-gradient(180deg, #9D79F4 0%, #7747FB 100%)",
            "&:hover": comment.trim()
              ? {
                  background: "linear-gradient(135deg,#6a5acd,#8a2be2)",
                }
              : {},
            "&.Mui-disabled": {
              background: "linear-gradient(180deg, #9D79F4 0%, #7747FB 100%)",
              color: "rgba(255, 255, 255, 1)",
              opacity: 0.6,
            },
          }}
        >
          {comment.trim() ? "Proceed" : "Proceed"}
        </Button>

        {/* Removed Skip Button */}
      </DialogContent>
    </Dialog>
  );
}
